import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  ModalBodyContent,
  ModalButtonsContainer,
  ModalCloseButtonV2,
  StyledModalBody,
  StyledModalTitle,
  StyledModalV2
} from '../../../../components/Modals';
import { Flex } from '../../../../components/ui/Flex';
import { closeActiveModal, openModal } from '../../../../helper/modals';
import styled, { css } from 'styled-components';
import { Fonts, Palette } from '../../../../components/utils';
import ModalCloseIcon from '../../../../assets/icons/modal-close-button.svg';
import { PiBankFill } from 'react-icons/pi';
import {
  GridContainer,
  LightBlueText,
  LoadingSpinner,
  OptionText,
  TitleText
} from '../../PaymentComponents';
import { PaymentStatusBadge } from '../../PaymentStatusBadge.jsx';
import { ButtonBodyVariant } from '../../../../components/ui/ButtonVariant';
import moment from 'moment';
import { StyledFormLabel } from '../../../../components/ui/Form';
import { FeatureSwitch } from '../../../offers/styles';
import { VOPAY_PAYMENT_TYPE } from '../../../../helper/constants';
import { cancelScheduledTransfer } from '../../../../redux/actions/payments';
import { changeOpenedModal } from '../../../../redux/actions/headerModal';
import { setToast } from '../../../../redux/actions/window';
import { store } from '../../../../index';
import {
  dispatchPaymentsPageRefetch,
  ensureBankLogosCached,
  fetchBuildingOneTimePaymentDetail,
  formatBuildingPaymentMethodDisplay,
  formatPaymentCreatedAtFromApi,
  mapOneTimePaymentDetailToModalPatch,
  normalizePaymentStatusKey,
  padBankLogoMetadataKey,
  paymentCanRefund
} from '../../PaymentUtils';

const PAYMENT_DETAILS_MODAL = 'PaymentDetailsSideModal';
const BANK_ENTRIES_STORAGE_KEY = 'grata:bank-logo-entries';

const PAYMENT_STATUS_PANEL_BG = '#ebf1f6';
const PAYMENT_DETAILS_SECTION_DIVIDER = '#e0e0e0';

function readStoredBankEntries() {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(BANK_ENTRIES_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (_e) {
    return {};
  }
}

function writeStoredBankEntry(key, entry) {
  if (typeof window === 'undefined' || !key || !entry || typeof entry !== 'object') return;
  try {
    const current = readStoredBankEntries();
    current[key] = entry;
    window.localStorage.setItem(BANK_ENTRIES_STORAGE_KEY, JSON.stringify(current));
  } catch (_e) {
    // Ignore storage quota / JSON errors.
  }
}

function deriveOneTimeReceiptDisplay(data) {
  if (data?.receipt_number) return data.receipt_number;
  const pid = data?.payment_id != null ? String(data.payment_id).trim() : '';
  if (pid && data?.created_at) {
    const y = moment.utc(data.created_at).year();
    return `RR-${y}-${pid}`;
  }
  return '';
}

function TenantSection({ t, residentDisplay, buildingDisplay }) {
  return (
    <div>
      <TitleText>{t('Tenant')}</TitleText>
      <GridContainer gap="0.25rem">
        <div>
          <OptionTitleText>{t('Resident')}</OptionTitleText>
          <ResidentNamesText>{residentDisplay}</ResidentNamesText>
        </div>
        <div>
          <OptionTitleText>{t('Building')}</OptionTitleText>
          <BuildingLineText>{buildingDisplay}</BuildingLineText>
        </div>
      </GridContainer>
    </div>
  );
}

function BankMethodBlock({ t, bankEntry, bankEntryLoading, methodDisplay, institutionLabel }) {
  if (bankEntryLoading) {
    return <OptionText cssOverride="text-transform: none;">{t('Loading bank details...')}</OptionText>;
  }
  return (
    <AccountInfoRow>
      {bankEntry?.logoURL ? (
        <BankLogoWrap>
          <img src={bankEntry.logoURL} alt="" />
        </BankLogoWrap>
      ) : (
        <BankLogoPlaceholder aria-hidden>
          <PiBankFill size={22} color="#143153" />
        </BankLogoPlaceholder>
      )}
      <AccountInfoTextBlock>
        <AccountTitleLine>{institutionLabel}</AccountTitleLine>
        <AccountMaskLine>{methodDisplay}</AccountMaskLine>
      </AccountInfoTextBlock>
    </AccountInfoRow>
  );
}

const paymentDetailsModalBodyCss = css`
  &.modal-body {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
`;

const ModalSectionDivider = styled.hr.attrs({ 'aria-hidden': true })`
  display: block;
  width: 100%;
  height: 1px;
  margin: 1.25rem 0;
  border: 0;
  background: ${PAYMENT_DETAILS_SECTION_DIVIDER};
  flex-shrink: 0;
`;

const OneTimeHeaderRoot = styled.div`
  &.modal-header {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0;
  }

  &&.modal-header {
    padding-right: 0 !important;
    padding-bottom: 24px !important;
    border-bottom: none !important;
  }
`;

const OneTimeHeaderTop = styled.div`
  align-self: stretch;
  box-sizing: border-box;
  padding: 0 0 20px 0;
  border-bottom: 1px solid ${PAYMENT_DETAILS_SECTION_DIVIDER};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`;

const OneTimePaymentModalTitle = styled(StyledModalTitle).attrs({ as: 'div' })`
  margin-bottom: 0;
  font-size: 25px;
  line-height: 40px;
  font-weight: 800;
`;

const OneTimeHeaderActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const ReceiptPill = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 20px;
  padding: 6px 14px;
  border: 1px solid ${Palette.BLACK_50};
  background: ${Palette.WHITE};
  color: ${Palette.DARK_ASH};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
`;

const RefundedPaymentNotice = styled.div`
  align-self: stretch;
  box-sizing: border-box;
  width: 100%;
  margin-top: 16px;
  padding: 12px 16px;
  border-radius: 8px;
  background: ${Palette.BACKGROUND_TAG_RED};
  border: 1px solid ${Palette.ERROR}33;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RefundedPaymentNoticeTitle = styled.span`
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 0.875rem;
  font-weight: 700;
  color: ${Palette.ERROR};
`;

const RefundedPaymentNoticeText = styled.span`
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${Palette.DARK_ASH};
`;

const OneTimeHeaderStatusBar = styled.div`
  align-self: stretch;
  box-sizing: border-box;
  width: 100%;
  margin-top: 16px;
  padding: 14px 16px;
  border-radius: 8px;
  background-color: ${PAYMENT_STATUS_PANEL_BG};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const PaymentStatusBarLabel = styled.span`
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 0.875rem;
  font-weight: 600;
  color: ${Palette.BLACK};
`;

const OneTimeStagesRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const oneTimeHeaderStatusBadgeCss = css`
  padding: 0 14px;
  height: 32px;
  font-weight: 600;
`;

function OneTimePaymentDetailsHeader({ data, t }) {
  const receipt = deriveOneTimeReceiptDisplay(data);
  const isRefunded = data?.has_refunded === true;
  const isRefund = data?.is_refund === true;
  const refundPaymentId =
    data?.refund_id != null && String(data.refund_id).trim() !== '' ? String(data.refund_id).trim() : '';

  return (
    <OneTimeHeaderRoot className="modal-header">
      <OneTimeHeaderTop>
        <div>
          <OneTimePaymentModalTitle>{t('Payment details')}</OneTimePaymentModalTitle>
        </div>
        <OneTimeHeaderActions>
          {receipt ? <ReceiptPill>{receipt}</ReceiptPill> : null}
          <PaymentStatusBadge
            modal
            status={data?.settlement_status}
            t={t}
            cssOverride={oneTimeHeaderStatusBadgeCss}
          />
          <ModalCloseButtonV2 type="button" onClick={() => closeActiveModal()} aria-label={t('Close')}>
            <img src={ModalCloseIcon} alt="close" />
          </ModalCloseButtonV2>
        </OneTimeHeaderActions>
      </OneTimeHeaderTop>
      <OneTimeHeaderStatusBar>
        <PaymentStatusBarLabel>{t('Payment Status')}</PaymentStatusBarLabel>
        <OneTimeStagesRow>
          {isRefund ? (
            <PaymentStatusBadge modal compact status={data?.refund_status} label={t('Refund')} t={t} />
          ) : (
            <>
              <PaymentStatusBadge modal compact status={data?.funding_status} label={t('Funding')} t={t} />
              <PaymentStatusBadge modal compact status={data?.transfer_status} label={t('Transfer')} t={t} />
              <PaymentStatusBadge modal compact status={data?.withdraw_status} label={t('Withdrawal')} t={t} />
            </>
          )}
        </OneTimeStagesRow>
      </OneTimeHeaderStatusBar>
      {isRefunded ? (
        <RefundedPaymentNotice>
          <RefundedPaymentNoticeTitle>{t('This payment has been refunded')}</RefundedPaymentNoticeTitle>
          {refundPaymentId ? (
            <RefundedPaymentNoticeText>
              {t('Refund payment ID')}: {refundPaymentId}
            </RefundedPaymentNoticeText>
          ) : null}
        </RefundedPaymentNotice>
      ) : null}
    </OneTimeHeaderRoot>
  );
}

function ScheduledPaymentDetailsHeader({ data, t }) {
  return (
    <OneTimeHeaderRoot className="modal-header">
      <OneTimeHeaderTop>
        <div>
          <OneTimePaymentModalTitle>{t('Schedule details')}</OneTimePaymentModalTitle>
        </div>
        <OneTimeHeaderActions>
          <PaymentStatusBadge
            modal
            status={data?.schedule_status}
            t={t}
            cssOverride={oneTimeHeaderStatusBadgeCss}
          />
          <ModalCloseButtonV2 type="button" onClick={() => closeActiveModal()} aria-label={t('Close')}>
            <img src={ModalCloseIcon} alt="close" />
          </ModalCloseButtonV2>
        </OneTimeHeaderActions>
      </OneTimeHeaderTop>
    </OneTimeHeaderRoot>
  );
}

const ConfirmDangerButton = styled(ButtonBodyVariant)`
  background-color: ${Palette.ERROR};
  color: ${Palette.WHITE};
  border-color: ${Palette.ERROR};

  :hover:not(:disabled) {
    color: ${Palette.WHITE};
    background-color: ${Palette.CONTENT_NEGATIVE};
    border-color: ${Palette.CONTENT_NEGATIVE};
  }

  :active:not(:disabled) {
    background-color: ${Palette.CONTENT_NEGATIVE};
    border-color: ${Palette.CONTENT_NEGATIVE};
  }
`;

const DetailLoadingHeader = styled.div`
  &.modal-header {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding-bottom: 0 !important;
    border-bottom: none !important;
  }
`;

const DetailLoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 6rem);
  width: 100%;
`;

const PaymentDetailsSideModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { modal, data } = useSelector((state) => state.headerModal);
  const isOpen = modal === PAYMENT_DETAILS_MODAL;

  const methodDisplay = formatBuildingPaymentMethodDisplay(
    data?.paymentMethodType,
    data?.paymentMethodLast4Digits
  );
  const [bankEntry, setBankEntry] = useState(null);
  const [bankEntryLoading, setBankEntryLoading] = useState(false);
  const [showCancelRecurringConfirm, setShowCancelRecurringConfirm] = useState(false);
  const [cancelRecurringLoading, setCancelRecurringLoading] = useState(false);

  const isScheduled = data?.payment_type === VOPAY_PAYMENT_TYPE.SCHEDULED;
  const recurringToggleOn = useMemo(() => {
    const key = normalizePaymentStatusKey(data?.schedule_status);
    return key === 'in_progress' || key === 'initiated';
  }, [data?.schedule_status]);
  const [recurringToggleChecked, setRecurringToggleChecked] = useState(recurringToggleOn);

  useEffect(() => {
    if (!isOpen) return;
    setRecurringToggleChecked(recurringToggleOn);
    setShowCancelRecurringConfirm(false);
    setCancelRecurringLoading(false);
  }, [isOpen, recurringToggleOn, data?.vopay_scheduled_transfers_id]);

  useEffect(() => {
    if (!isOpen) return undefined;
    void ensureBankLogosCached();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setBankEntry(null);
      setBankEntryLoading(false);
      return undefined;
    }

    const key = padBankLogoMetadataKey(data?.institution_number);
    if (!key) {
      setBankEntry(null);
      setBankEntryLoading(false);
      return undefined;
    }

    let cancelled = false;
    setBankEntryLoading(true);

    const storedEntries = readStoredBankEntries();
    const stored = storedEntries[key];
    if (stored && typeof stored === 'object') {
      setBankEntry(stored);
      setBankEntryLoading(false);
    }

    ensureBankLogosCached()
      .then((map) => {
        if (cancelled) return;
        const fromMap = map[key] ? map[key] : null;
        const fallback = readStoredBankEntries()[key];
        const next = fromMap || (fallback && typeof fallback === 'object' ? fallback : null);
        setBankEntry(next);
        if (fromMap) writeStoredBankEntry(key, fromMap);
        setBankEntryLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        const fallback = readStoredBankEntries()[key];
        setBankEntry(fallback && typeof fallback === 'object' ? fallback : null);
        setBankEntryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    data?.institution_number,
    data?.payment_type,
    data?.vopay_scheduled_transfers_id,
    data?.payment_id
  ]);

  useEffect(() => {
    if (!isOpen) return undefined;
    if (data?.payment_type !== VOPAY_PAYMENT_TYPE.ONE_TIME) return undefined;
    if (data?.one_time_detail_loaded) return undefined;

    const markDetailLoaded = (patch = {}) => {
      const { modal: m, data: prev } = store.getState().headerModal;
      if (m !== PAYMENT_DETAILS_MODAL || !prev) return;
      if (prev.payment_type !== VOPAY_PAYMENT_TYPE.ONE_TIME) return;
      if (prev.one_time_detail_loaded) return;
      dispatch(
        changeOpenedModal({
          modal: PAYMENT_DETAILS_MODAL,
          data: { ...prev, ...patch, one_time_detail_loaded: true }
        })
      );
    };

    const bid = Number(data?.building_id);
    const uid = Number(data?.unit_id);
    const pidRaw = data?.payment_id;
    const pid =
      pidRaw != null && String(pidRaw).trim() !== '' ? parseInt(String(pidRaw).trim(), 10) : NaN;
    if (!bid || !uid || !Number.isFinite(pid) || pid <= 0) {
      markDetailLoaded();
      return undefined;
    }

    let cancelled = false;
    fetchBuildingOneTimePaymentDetail(bid, uid, pid)
      .then((detail) => {
        if (cancelled) return;
        const { modal: m, data: prev } = store.getState().headerModal;
        if (m !== PAYMENT_DETAILS_MODAL || !prev) return;
        if (prev.payment_type !== VOPAY_PAYMENT_TYPE.ONE_TIME) return;
        if (prev.one_time_detail_loaded) return;
        if (Number(prev.building_id) !== bid || Number(prev.unit_id) !== uid) return;
        const prevPid =
          prev.payment_id != null && String(prev.payment_id).trim() !== ''
            ? parseInt(String(prev.payment_id).trim(), 10)
            : NaN;
        if (prevPid !== pid) return;

        dispatch(
          changeOpenedModal({
            modal: PAYMENT_DETAILS_MODAL,
            data: { ...prev, ...mapOneTimePaymentDetailToModalPatch(detail) }
          })
        );
      })
      .catch((err) => {
        if (cancelled) return;
        markDetailLoaded();
        dispatch(
          setToast({
            toastShow: true,
            toastMessage: t('Could not load payment details: {{error}}', {
              error: err?.response?.data?.message || err?.message || t('Unknown error')
            }),
            modal: 'errorToast'
          })
        );
      });

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    data?.payment_type,
    data?.building_id,
    data?.unit_id,
    data?.payment_id,
    data?.one_time_detail_loaded,
    dispatch,
    t
  ]);

  const residentDisplay = useMemo(() => {
    const tenants = Array.isArray(data?.tenants) ? data.tenants.filter(Boolean) : [];
    if (tenants.length) return tenants.join('\n');
    return data?.user_name || '—';
  }, [data?.tenants, data?.user_name]);

  const buildingDisplay = useMemo(() => {
    const addr = String(data?.building_address || '').trim();
    const unit = String(data?.unit_num || '').trim();
    const unitPart = unit ? `${t('Unit')} ${unit}` : '';
    if (addr && unitPart) return `${addr} | ${unitPart}`;
    if (addr) return addr;
    if (unitPart) return `${data?.building_name || ''}${data?.building_name ? ' | ' : ''}${unitPart}`.trim() || '—';
    return data?.building_name || '—';
  }, [data?.building_address, data?.building_name, data?.unit_num, t]);

  const scheduleStartDateDisplay = useMemo(() => {
    const value = data?.schedule_start_date;
    if (!value) return '—';
    const parsed = moment.utc(value).local();
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '—';
  }, [data?.schedule_start_date]);

  const scheduleEndDateDisplay = useMemo(() => {
    const value = data?.schedule_end_date;
    if (!value) return '—';
    const parsed = moment.utc(value).local();
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '—';
  }, [data?.schedule_end_date]);

  const onRecurringToggle = (e) => {
    const next = e?.target?.checked;
    if (next) return;
    if (!recurringToggleChecked) return;
    if (!data?.can_cancel_recurring) return;
    setShowCancelRecurringConfirm(true);
  };

  const confirmCancelRecurring = () => {
    if (cancelRecurringLoading) return;
    setCancelRecurringLoading(true);
    dispatch(
      cancelScheduledTransfer({
        body: {
          ScheduledTransferID: String(data?.vopay_scheduled_transfers_id ?? ''),
          BuildingID: Number(data?.building_id)
        },
        success: () => {
          setRecurringToggleChecked(false);
          setShowCancelRecurringConfirm(false);
          setCancelRecurringLoading(false);
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Scheduled payment has been successfully cancelled')
            })
          );
          dispatchPaymentsPageRefetch();
        },
        fail: (err) => {
          setCancelRecurringLoading(false);
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Could not cancel transaction: {{error}}', {
                error: err?.data?.message || t('Unknown error')
              }),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  };

  const recurringToggleDisabled =
    normalizePaymentStatusKey(data?.schedule_status) === 'cancelled' ||
    !data?.can_cancel_recurring ||
    cancelRecurringLoading;

  const bankInstitutionLabel = bankEntryLabel(data, bankEntry);
  const isOneTimeDetailLoading = !isScheduled && !data?.one_time_detail_loaded;
  const showDepositAmount =
    !isScheduled && data?.one_time_detail_loaded && data?.deposit_amount != null;
  const showDepositRefund = showDepositAmount && paymentCanRefund(data);

  return (
    <StyledModalV2
      show={isOpen}
      onClose={closeActiveModal}
      closeOnBackdrop={false}
      width={'550px'}
      side
    >
      {isOneTimeDetailLoading ? (
        <>
          <DetailLoadingHeader className="modal-header">
            <ModalCloseButtonV2 type="button" onClick={() => closeActiveModal()} aria-label={t('Close')}>
              <img src={ModalCloseIcon} alt="close" />
            </ModalCloseButtonV2>
          </DetailLoadingHeader>
          <StyledModalBody cssOverride={paymentDetailsModalBodyCss}>
            <DetailLoadingWrap role="status" aria-live="polite" aria-label={t('Loading')}>
              <LoadingSpinner size={32} color={Palette.BLACK} />
            </DetailLoadingWrap>
          </StyledModalBody>
        </>
      ) : (
        <>
          <StyledModalBody cssOverride={paymentDetailsModalBodyCss}>
            {isScheduled ? (
              <ScheduledPaymentDetailsHeader data={data} t={t} />
            ) : (
              <OneTimePaymentDetailsHeader data={data} t={t} />
            )}
            <ModalBodyContent>
              <Flex direction="column" gap="1rem">
                {isScheduled ? (
                  <Flex direction="column" gap={0}>
                    <TenantSection t={t} residentDisplay={residentDisplay} buildingDisplay={buildingDisplay} />

                    <ModalSectionDivider />

                    <div>
                      <TitleText>{t('Schedule')}</TitleText>
                      <GridContainer gap="0.25rem">
                        <div>
                          <OptionTitleText>{t('Amount')}</OptionTitleText>
                          <OptionText cssOverride="text-transform: none;">
                            {`$${data?.amount?.toFixed(2)}` || '—'}
                          </OptionText>
                        </div>
                        <div>
                          <OptionTitleText>{t('Frequency')}</OptionTitleText>
                          <OptionText cssOverride="text-transform: capitalize;">{data?.frequency || '—'}</OptionText>
                        </div>
                        <div>
                          <OptionTitleText>{t('Start Date')}</OptionTitleText>
                          <OptionText cssOverride="text-transform: none;">{scheduleStartDateDisplay}</OptionText>
                        </div>
                        <div>
                          <OptionTitleText>{t('End Date')}</OptionTitleText>
                          <OptionText cssOverride="text-transform: none;">{scheduleEndDateDisplay}</OptionText>
                        </div>
                      </GridContainer>
                    </div>

                    <ModalSectionDivider />

                    <Flex direction="column" gap="0.75rem">
                      {recurringToggleOn ? (
                        <>
                          <TitleText>{t('Payment Method')}</TitleText>
                          <BankMethodBlock
                            t={t}
                            bankEntry={bankEntry}
                            bankEntryLoading={bankEntryLoading}
                            methodDisplay={methodDisplay}
                            institutionLabel={bankInstitutionLabel}
                          />
                        </>
                      ) : null}

                      <RecurringToggleRow>
                        <div>
                          <RecurringLabel>{t('Recurring Payment')}</RecurringLabel>
                          <RecurringDescription>
                            {t('Automatically charge this bank account.')}
                          </RecurringDescription>
                        </div>
                        <FeatureSwitch
                          shape="pill"
                          color="primary"
                          checked={recurringToggleChecked}
                          disabled={recurringToggleDisabled}
                          onChange={onRecurringToggle}
                        />
                      </RecurringToggleRow>
                      {showCancelRecurringConfirm ? (
                        <CancelRecurringConfirmCard>
                          <CancelRecurringConfirmText>
                            {t(
                              'Disabling recurring payments will permanently remove the payment schedule. This action cannot be undone.'
                            )}
                          </CancelRecurringConfirmText>
                          <CancelRecurringConfirmActions>
                            <ButtonBodyVariant
                              variant="secondary"
                              size="sm"
                              onClick={() => setShowCancelRecurringConfirm(false)}
                              disabled={cancelRecurringLoading}
                            >
                              {t('Cancel')}
                            </ButtonBodyVariant>
                            <ConfirmDangerButton
                              size="sm"
                              onClick={() => {
                                if (cancelRecurringLoading) return;
                                confirmCancelRecurring();
                                closeActiveModal();
                              }}
                              disabled={cancelRecurringLoading}
                            >
                              {cancelRecurringLoading ? t('Confirming...') : t('Confirm')}
                            </ConfirmDangerButton>
                          </CancelRecurringConfirmActions>
                        </CancelRecurringConfirmCard>
                      ) : null}
                    </Flex>
                  </Flex>
                ) : (
                  <Flex direction="column" gap={0}>
                    <TenantSection t={t} residentDisplay={residentDisplay} buildingDisplay={buildingDisplay} />

                    <ModalSectionDivider />

                    <div>
                      <TitleText>{t('Transaction')}</TitleText>
                      <GridContainer gap="0.25rem">
                        <div>
                          <OptionTitleText>{t('Amount')}</OptionTitleText>
                          <OptionText cssOverride="text-transform: none;">
                            {`$${data?.amount?.toFixed(2)}` || '—'}
                          </OptionText>
                        </div>
                        <div>
                          <OptionTitleText>{t('Purpose')}</OptionTitleText>
                          <OptionText cssOverride="text-transform: none;">{data?.payment_purpose || '—'}</OptionText>
                        </div>
                        <div>
                          <OptionTitleText>{t('Date Updated')}</OptionTitleText>
                          <OptionText cssOverride="text-transform: none;">
                            {data?.updated_at
                              ? formatPaymentCreatedAtFromApi(data.updated_at, 'MMM D, YYYY') || '—'
                              : '—'}
                          </OptionText>
                        </div>
                        {showDepositAmount ? (
                          <div>
                            <OptionTitleText>{t('Deposit Amount')}</OptionTitleText>
                            <OptionText cssOverride="text-transform: none;">
                              {`$${data.deposit_amount.toFixed(2)}`}
                            </OptionText>
                          </div>
                        ) : null}
                      </GridContainer>
                    </div>

                    <ModalSectionDivider />

                    <Flex direction="column" gap="0.75rem">
                      <TitleText>{t('Payment Method')}</TitleText>
                      <BankMethodBlock
                        t={t}
                        bankEntry={bankEntry}
                        bankEntryLoading={bankEntryLoading}
                        methodDisplay={methodDisplay}
                        institutionLabel={bankInstitutionLabel}
                      />
                    </Flex>
                  </Flex>
                )}
              </Flex>
            </ModalBodyContent>
          </StyledModalBody>

          <ModalButtonsContainer gap="0.5rem" isAtBottom>
            <FooterActions>
              <FooterButtonRow>
                <ButtonBodyVariant variant="secondary" cssOverride="min-width: 120px;" onClick={closeActiveModal}>
                  {t('Back')}
                </ButtonBodyVariant>
                {/* Temporarily hidden for all payments
                {showDepositRefund ? (
                  <ButtonBodyVariant
                    type="button"
                    cssOverride="min-width: 120px; white-space: nowrap;"
                    onClick={() => openModal('RefundPaymentConfirmation', data)}
                  >
                    {t('Return Deposit')}
                  </ButtonBodyVariant>
                ) : null}
                */}
                {!isScheduled ? (
                  <ButtonBodyVariant
                    type="button"
                    cssOverride="min-width: 120px; white-space: nowrap;"
                    onClick={() => openModal('PaymentReceiptSideModal', data)}
                  >
                    {t(showDepositRefund ? 'Receipt' : 'View Tenant Receipt')}
                  </ButtonBodyVariant>
                ) : null}
                {isScheduled && !recurringToggleOn ? (
                  <ButtonBodyVariant
                    type="button"
                    cssOverride="min-width: 120px; white-space: nowrap;"
                    onClick={() => {
                      closeActiveModal();
                      openModal('CreatePaymentModal');
                    }}
                  >
                    {t('Create New Payment')}
                  </ButtonBodyVariant>
                ) : null}
              </FooterButtonRow>
            </FooterActions>
          </ModalButtonsContainer>
        </>
      )}
    </StyledModalV2>
  );
};

function bankEntryLabel(data, bankEntry) {
  const shortName = bankEntry?.institutionNameShort || data?.bankNameShort;
  const fullName = bankEntry?.institutionName || data?.bankName;
  if (shortName && shortName !== '-') return shortName;
  return fullName || '—';
}

const AccountInfoRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const BankLogoWrap = styled.div`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 6px;
  overflow: hidden;
  background: ${Palette.WHITE};
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const BankLogoPlaceholder = styled.div`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 6px;
  background: ${Palette.CONTENT_TERTIARY}22;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Palette.CONTENT_TERTIARY};
`;

const AccountInfoTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const AccountTitleLine = styled(OptionText)`
  color: ${Palette.CONTENT_TERTIARY};
  text-transform: none;
`;

const AccountMaskLine = styled(OptionText)`
  color: ${Palette.OCEANIC};
  text-transform: none;
`;

const RecurringToggleRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
`;

const RecurringLabel = styled(StyledFormLabel)`
  font-weight: 600;
  margin-bottom: 0;
`;

const RecurringDescription = styled(OptionText)`
  color: ${Palette.CONTENT_TERTIARY};
  font-size: 0.8125rem;
  line-height: 1.35;
  text-transform: none;
`;

const CancelRecurringConfirmCard = styled.div`
  background: ${Palette.BACKGROUND_TAG_RED};
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CancelRecurringConfirmText = styled(OptionText)`
  color: ${Palette.ERROR};
  text-transform: none;
  line-height: 1.4rem;
`;

const CancelRecurringConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const ResidentNamesText = styled(OptionText)`
  line-height: 1.4;
  white-space: pre-line;
  text-transform: none;
`;

const BuildingLineText = styled(OptionText)`
  line-height: 1.4;
  text-transform: none;
`;

const OptionTitleText = styled(LightBlueText)`
  font-size: 0.75rem;
  line-height: 1.5rem;
`;

const FooterActions = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  position: relative;
  padding-top: 1rem;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -24px;
    right: -24px;
    border-top: 1px solid ${Palette.BORDER_OUTLINE};
  }
`;

const FooterButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.5rem;
  width: 100%;
`;

export default PaymentDetailsSideModal;
